import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import FlipMove from "react-flip-move"

import { getUserArchivedApplets, restoreApplet } from "shared/lib/graphql"
import { rejectedXHRPromiseLogger } from "shared/lib/xhr_promise"
import Search from "../my_applets/search"
import AppletCard from "./applet_card"
import Spinner from "shared/components/spinner"
import SearchIndex from "shared/lib/search_index"
import logIFTTTNextAction from "app/lib/ifttt_next_log_action"
import { captureMessage } from "@sentry/react"

import "./index.scss"

const AppletCardWithRef = React.forwardRef((props, ref) => (
  <div ref={ref}>
    <AppletCard {...props} />
  </div>
))
AppletCardWithRef.displayName = "AppletCardWithRef"

const PER_PAGE = 24

export default function ArchiveApplets({ proBannerUrls, user }) {
  const { appletQuota, isPro, login } = user
  const [applets, setApplets] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [searchResults, setSearchResults] = useState([])
  const [restoringApplet, setRestoringApplet] = useState(false)
  const [usedAppletsCount, setUsedAppletsCount] = useState(
    appletQuota.total - appletQuota.remaining
  )
  const [appletsIndex] = useState(new SearchIndex())
  const [offset, setOffset] = useState(0)
  const [pageEndReached, setPageEndReached] = useState(false)

  useEffect(() => {
    if (applets === null) {
      return
    } else if (searchTerm) {
      const appletIds = appletsIndex.search(searchTerm)
      setSearchResults(applets.filter(applet => appletIds.includes(applet.id)))
    } else {
      setSearchResults(applets)
    }

    // Resize window to fix positioning issues
    window.dispatchEvent(new Event("resize"))
  }, [applets, searchTerm])

  const loadNextPage = async () => {
    const { applets } = await getUserArchivedApplets(login, PER_PAGE, offset)
    const connections = applets
      .map(a => a.normalized_applet)
      .map(c => ({
        ...c,
        service_names: c.channels.map(ch => ch.name),
      }))

    setOffset(offset + PER_PAGE)
    if (connections.length < PER_PAGE) {
      setPageEndReached(true)
    }
    return connections
  }

  const handleShowMore = async () => {
    const connections = await loadNextPage()
    setApplets([...applets, ...connections])
  }

  useEffect(() => {
    loadNextPage()
      .then(connections => {
        window.App.pageViewData = window.App.pageViewData || {}
        window.App.pageViewData.applet_ids = connections.map(c => c.id)
        setApplets(connections)
        connections.forEach(doc => {
          appletsIndex.add(doc.id, doc.name, [
            ...doc.service_names,
            ...(doc.description || "").split(" "),
          ])
        })
      })
      .catch(
        rejectedXHRPromiseLogger(() => {
          setApplets([])
        })
      )
  }, [])

  const onRestoreApplet = applet => {
    if (restoringApplet) return null

    setRestoringApplet(true)

    restoreApplet(applet.id)
      .then(data => {
        const { errors, normalized_applet } = data.restorePersonalApplet

        if (errors.length > 0 || normalized_applet.archived) {
          if (normalized_applet.archived) {
            const msg =
              "Restore Applet mutation success but Applet continues archived"
            if (window.App.allowDebugErrorMessages) {
              console.warn("catch", msg)
            }
            captureMessage(msg)
          }

          if (errors[0].message === "You cannot update this applet.") {
            window.appendFlash(errors[0].message, "danger")
          } else {
            window.appendFlash(
              "Could not restore your Applet. Please try again later",
              "danger"
            )
          }
        } else if (!normalized_applet.archived) {
          setApplets(applets.filter(a => a.id !== applet.id))

          setUsedAppletsCount(usedAppletsCount + 1)
          window.appendFlash("Applet restored successfully")

          logIFTTTNextAction("statechange", {
            location_type: "archive",
            object_id: applet.id,
            object_type: "connection",
            state: "unarchived",
          })
        }
      })
      .catch(
        rejectedXHRPromiseLogger(() => {
          window.appendFlash(
            "Could not restore your Applet. Please try again later",
            "danger"
          )
        })
      )
      .finally(() => {
        setRestoringApplet(false)
      })
  }

  const remainingQuota = appletQuota.total - usedAppletsCount
  const availableToRestore = isPro || remainingQuota > 0

  if (applets === null) {
    return <Spinner />
  } else if (applets.length) {
    return (
      <>
        <h1>Archive</h1>

        <Search onSearch={setSearchTerm} value={searchTerm} />

        <section className="dashboard-applets">
          {searchResults.length > 0 ? (
            <FlipMove typeName="ul" className="web-applet-cards">
              {searchResults.map(applet => (
                <AppletCardWithRef
                  key={applet.id}
                  applet={applet}
                  isPro={isPro}
                  availableToRestore={availableToRestore}
                  onRestoreApplet={onRestoreApplet}
                  subscriptionUrl={proBannerUrls.subscriptionUrl}
                />
              ))}
            </FlipMove>
          ) : (
            <p styleName="empty-search">
              We couldn&apos;t find anything matching {searchTerm}
            </p>
          )}
        </section>
        {!pageEndReached && (
          <button
            styleName="show-more"
            className="button-secondary"
            onClick={handleShowMore}
          >
            Show more
          </button>
        )}

        <div className="breath-space" />
      </>
    )
  } else {
    return (
      <>
        <h1>Archive</h1>
        <section styleName="empty-state">
          <p>
            You have no Archived Applets right now. <br />
            You can see archived Applets and restore when needed.
          </p>
          <span>
            Currently, Archive only shows archived Applets created by you.
            <br />
            Archive for discoverable Applets coming soon...
          </span>
        </section>
      </>
    )
  }
}

ArchiveApplets.propTypes = {
  proBannerUrls: PropTypes.object.isRequired,
  user: PropTypes.shape({
    appletQuota: PropTypes.shape({
      total: PropTypes.number.isRequired,
    }),
    isPro: PropTypes.bool.isRequired,
    login: PropTypes.string.isRequired,
    unlimitedAppletEnables: PropTypes.bool,
  }).isRequired,
}

ArchiveApplets.defaultPropTypes = {
  isPro: false,
}
